/**
 * Eligibility work queue table — standalone, no dependency on episode-tracker.
 */

import {
  Anchor,
  Box,
  Button,
  Group,
  Pagination,
  Select,
  Table,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { IconArrowDown, IconArrowUp } from '@tabler/icons-react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { SearchInput } from '../../components/SearchInput';
import type { EligibilityRow } from './types';
import { PresenceAvatars } from '@/components/presence/PresenceAvatars';
import type { PresenceActor } from '@/lib/presence/presenceFhir';
import { presenceRowKey } from '@/lib/presence/presenceFhir';

// ---------------------------------------------------------------------------
// Column definitions
// ---------------------------------------------------------------------------

interface ColDef {
  key: keyof EligibilityRow;
  label: string;
}

const COLUMNS: ColDef[] = [
  { key: 'submissionDate', label: 'Submission Date' },
  { key: 'clientName', label: 'Client Name' },
  { key: 'serviceTypeCodes', label: 'Service type code' },
  { key: 'payer', label: 'Payer' },
  { key: 'memberId', label: 'Member ID' },
  { key: 'soharErrorReason', label: 'Sohar Error Reason' },
  { key: 'assumedCopay', label: 'Assumed Copay' },
  { key: 'soonestBookedAppointment', label: 'Next Appointment' },
  { key: 'daysUntilAppointment', label: 'Days Until' },
];

const SERVICE_TYPE_OPTIONS = [
  { value: 'PSY', label: 'PSY (Psychotherapy)' },
  { value: 'IOP', label: 'IOP' },
  { value: 'PCH', label: 'PCH (Psychiatry)' },
];

const ROWS_PER_PAGE_OPTIONS = [
  { value: '10', label: '10' },
  { value: '25', label: '25' },
  { value: '50', label: '50' },
  { value: '100', label: '100' },
];

// ---------------------------------------------------------------------------
// Sorting
// ---------------------------------------------------------------------------

type SortDir = 'asc' | 'desc';

function getSortValue(row: EligibilityRow, key: keyof EligibilityRow): string | number | null {
  const v = row[key];
  if (v == null) return null;
  if (typeof v === 'number' && !Number.isNaN(v)) return v;
  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}/.test(v)) return new Date(v).getTime();
  if (Array.isArray(v)) return (v as string[]).join(', ');
  return String(v);
}

function compareRows(a: EligibilityRow, b: EligibilityRow, key: keyof EligibilityRow, dir: SortDir): number {
  const va = getSortValue(a, key);
  const vb = getSortValue(b, key);
  if (va == null && vb == null) return 0;
  if (va == null) return dir === 'asc' ? 1 : -1;
  if (vb == null) return dir === 'asc' ? -1 : 1;
  let cmp = 0;
  if (typeof va === 'number' && typeof vb === 'number') {
    cmp = va - vb;
  } else {
    cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
  }
  return dir === 'asc' ? cmp : -cmp;
}

// ---------------------------------------------------------------------------
// Cell formatting
// ---------------------------------------------------------------------------

const US_DATE: Intl.DateTimeFormatOptions = { month: 'numeric', day: 'numeric', year: 'numeric' };

function formatCell(_key: keyof EligibilityRow, value: EligibilityRow[keyof EligibilityRow]): string {
  if (value == null) return '—';
  if (Array.isArray(value)) return (value as string[]).join(', ');
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
    try { return new Date(value).toLocaleDateString('en-US', US_DATE); } catch { /* fall through */ }
  }
  return String(value) || '—';
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export interface EligibilityTableProps {
  rows: EligibilityRow[];
  onAction: (row: EligibilityRow) => void;
  /** Live presence bubbles keyed by row id (ServiceRequest.id). */
  presenceByRow?: Map<string, PresenceActor[]>;
}

const TABLE_ROW_HEIGHT_PX = 44;

export function EligibilityTable({ rows, onAction, presenceByRow }: EligibilityTableProps): JSX.Element {
  const [search, setSearch] = useState('');
  const [payerFilter, setPayerFilter] = useState<string | null>(null);
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<keyof EligibilityRow | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState('10');
  const [measuredRowHeightPx, setMeasuredRowHeightPx] = useState<number | null>(null);
  const tableWrapperRef = useRef<HTMLDivElement>(null);

  // Build payer options dynamically from the live row data.
  const payerOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { value: string; label: string }[] = [];
    for (const row of rows) {
      if (row.payer && !seen.has(row.payer)) {
        seen.add(row.payer);
        options.push({ value: row.payer, label: row.payer });
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [rows]);

  const filteredRows = useMemo(() => {
    let result = rows;

    if (payerFilter) {
      result = result.filter((r) => r.payer === payerFilter);
    }

    if (serviceTypeFilter) {
      result = result.filter((r) =>
        r.serviceTypeCodes?.includes(serviceTypeFilter),
      );
    }

    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter((r) =>
        [r.clientName, r.payer, r.memberId, r.soharErrorReason]
          .some((v) => typeof v === 'string' && v.toLowerCase().includes(s)),
      );
    }

    return result;
  }, [rows, search, payerFilter, serviceTypeFilter]);

  const sortedRows = useMemo(() => {
    if (!sortKey) return filteredRows;
    return [...filteredRows].sort((a, b) => compareRows(a, b, sortKey, sortDir));
  }, [filteredRows, sortKey, sortDir]);

  const itemsPerPage = parseInt(rowsPerPage || '10', 10);
  const totalPages = Math.max(1, Math.ceil(sortedRows.length / itemsPerPage));
  const safePage = Math.min(Math.max(1, page), totalPages);

  useEffect(() => {
    if (page !== safePage) setPage(safePage);
  }, [page, safePage]);

  const paginatedRows = useMemo(
    () => sortedRows.slice((safePage - 1) * itemsPerPage, safePage * itemsPerPage),
    [sortedRows, safePage, itemsPerPage],
  );

  const rangeStart = sortedRows.length === 0 ? 0 : (safePage - 1) * itemsPerPage + 1;
  const rangeEnd = Math.min(safePage * itemsPerPage, sortedRows.length);

  useLayoutEffect(() => {
    if (paginatedRows.length === 0) return;
    const firstRow = tableWrapperRef.current?.querySelector('tbody tr');
    if (firstRow) setMeasuredRowHeightPx(Math.round(firstRow.getBoundingClientRect().height));
  }, [paginatedRows.length]);

  const handleSort = (key: keyof EligibilityRow): void => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
      if (sortDir === 'desc') setSortKey(null);
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  return (
    <>
      <Group mb="md" gap="sm" wrap="wrap">
        <SearchInput
          placeholder="Search by name, email, payer..."
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          w={280}
        />
        <Select
          placeholder="Payer"
          data={payerOptions}
          value={payerFilter}
          onChange={(v) => { setPayerFilter(v); setPage(1); }}
          clearable
          w={220}
        />
        <Select
          placeholder="Service type"
          data={SERVICE_TYPE_OPTIONS}
          value={serviceTypeFilter}
          onChange={(v) => { setServiceTypeFilter(v); setPage(1); }}
          clearable
          w={220}
        />
      </Group>

      <Box ref={tableWrapperRef} style={{ overflowX: 'auto' }}>
        <Table
          highlightOnHover
          verticalSpacing="sm"
          horizontalSpacing="sm"
          style={{ tableLayout: 'fixed', width: '100%' }}
        >
          <Table.Thead>
            <Table.Tr>
              {COLUMNS.map((col) => (
                <Table.Th key={col.key} style={{ verticalAlign: 'middle' }}>
                  <UnstyledButton
                    onClick={() => handleSort(col.key)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
                  >
                    <Text size="sm" fw={600} component="span" lineClamp={2}>
                      {col.label}
                    </Text>
                    {sortKey === col.key
                      ? sortDir === 'asc' ? <IconArrowUp size={14} /> : <IconArrowDown size={14} />
                      : null}
                  </UnstyledButton>
                </Table.Th>
              ))}
              <Table.Th w={140} style={{ verticalAlign: 'middle' }} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {paginatedRows.length === 0 ? (
              <Table.Tr>
                <Table.Td colSpan={COLUMNS.length + 1}>
                  <Text size="sm" c="dimmed" py="xl" ta="center">
                    No eligibility records
                  </Text>
                </Table.Td>
              </Table.Tr>
            ) : (
              paginatedRows.map((row) => (
                <Table.Tr key={row.id}>
                  {COLUMNS.map((col) => (
                    <Table.Td key={col.key} style={{ verticalAlign: 'middle', wordBreak: 'break-word' }}>
                      {col.key === 'clientName' ? (
                        <Anchor
                          component={Link}
                          to={`/dashboard/clients/${row.clientId}`}
                          fw={500}
                          size="sm"
                          lineClamp={3}
                        >
                          {row.clientName}
                        </Anchor>
                      ) : (
                        <Text size="sm" lineClamp={4}>
                          {formatCell(col.key, row[col.key])}
                        </Text>
                      )}
                    </Table.Td>
                  ))}
                  <Table.Td style={{ verticalAlign: 'middle', textAlign: 'right', width: 140 }}>
                    <Group gap="xs" justify="flex-end" wrap="nowrap">
                      <PresenceAvatars actors={presenceByRow?.get(presenceRowKey('tracker', String(row.id))) ?? []} />
                      <Button variant="subtle" size="xs" onClick={() => onAction(row)}>
                        Action
                      </Button>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))
            )}
          </Table.Tbody>
        </Table>
      </Box>

      {paginatedRows.length > 0 && paginatedRows.length < itemsPerPage && (
        <Box
          style={{ height: (itemsPerPage - paginatedRows.length) * (measuredRowHeightPx ?? TABLE_ROW_HEIGHT_PX) }}
          aria-hidden
        />
      )}

      <Group justify="space-between" mt="md">
        <Group gap="xs">
          <Text size="sm" c="dimmed">Rows per page:</Text>
          <Select
            value={rowsPerPage}
            onChange={(v) => { setRowsPerPage(v ?? '10'); setPage(1); }}
            data={ROWS_PER_PAGE_OPTIONS}
            w={80}
            size="xs"
          />
          <Text size="sm" c="dimmed">
            {sortedRows.length === 0 ? '0-0 of 0' : `${rangeStart}-${rangeEnd} of ${sortedRows.length}`}
          </Text>
        </Group>
        <Pagination
          total={totalPages}
          value={safePage}
          onChange={setPage}
          size="xs"
          aria-label="Table pagination"
        />
      </Group>
    </>
  );
}
